/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Spin } from 'antd';
import { ChartDataItem, StackedSeriesItem } from '../../services/statistics.service';

export interface AccessOverviewChartsProps {
  loading?: boolean;
  totalAccess?: number;
  featureChartData?: ChartDataItem[];
  trendChartData?: {
    isDaily: boolean;
    categories: string[];
    series: StackedSeriesItem[];
  };
}

const FEATURE_COLORS: Record<string, string> = {
  'Trang chủ': '#5B7BFE',
  'Theo dõi sức khỏe': '#54C882',
  'Tư vấn sức khỏe': '#FA983A',
  'Kiểm tra & Lên bữa ăn': '#20D4D2',
  'Nhật ký sức khỏe': '#00D0FF',
  'Mục tiêu của bạn': '#8B5CF6',
  'Cẩm nang cho bạn': '#FEDC77',
  'Khác': '#9CA3AF',
};

const COLOR_PALETTE = [
  '#5B7BFE',
  '#54C882',
  '#FA983A',
  '#20D4D2',
  '#00D0FF',
  '#8B5CF6',
  '#FEDC77',
  '#9CA3AF',
];

export const FeatureDoughnutChartCard: React.FC<{
  loading?: boolean;
  totalAccess?: number;
  featureChartData?: ChartDataItem[];
}> = ({ loading = false, totalAccess = 0, featureChartData = [] }) => {
  const formatNumber = (num: number) => num.toLocaleString('vi-VN');

  const getDoughnutOption = () => {
    return {
      title: {
        text: `${formatNumber(totalAccess)}\nlượt`,
        left: 'center',
        top: '38%',
        textStyle: {
          fontSize: 22,
          fontWeight: 'bold',
          color: '#262626',
          lineHeight: 28,
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          `<b>${params.name}</b><br/>Lượt truy cập: ${formatNumber(params.value)} (${params.percent}%)`,
      },
      legend: {
        orient: 'horizontal',
        bottom: '2%',
        left: 'center',
        itemGap: 14,
        itemWidth: 8,
        itemHeight: 8,
        icon: 'circle',
        textStyle: {
          fontSize: 12,
          color: '#595959',
        },
      },
      series: [
        {
          name: 'Chức năng',
          type: 'pie',
          radius: ['52%', '76%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 0,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) =>
              params.percent > 4 ? `${params.percent}%\n(${formatNumber(params.value)})` : '',
            fontSize: 10,
            color: '#fff',
            fontWeight: '600',
            lineHeight: 12,
          },
          data: featureChartData.map((item, idx) => ({
            name: item.name,
            value: item.value,
            itemStyle: {
              color: FEATURE_COLORS[item.name] || COLOR_PALETTE[idx % COLOR_PALETTE.length],
            },
          })),
        },
      ],
    };
  };

  return (
    <Card css={cardStyle}>
      <Spin spinning={loading}>
        <div css={cardHeaderStyle}>
          Lượt truy cập theo chức năng
        </div>
        <div style={{ padding: '16px 20px 8px' }}>
          <ReactECharts
            option={getDoughnutOption()}
            style={{ height: '320px', width: '100%' }}
            notMerge={true}
          />
        </div>
      </Spin>
    </Card>
  );
};

export const TrendLineChartCard: React.FC<{
  loading?: boolean;
  trendChartData?: {
    isDaily: boolean;
    categories: string[];
    series: StackedSeriesItem[];
  };
}> = ({ loading = false, trendChartData = { isDaily: true, categories: [], series: [] } }) => {
  const formatNumber = (num: number) => num.toLocaleString('vi-VN');

  const getLineOption = () => {
    const categories = trendChartData.categories || [];
    const hasData = categories.length > 0;
    const hasZoom = categories.length > 10;

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any[]) => {
          if (!params || params.length === 0) return '';
          let total = 0;
          params.forEach((p) => (total += Number(p.value) || 0));
          let html = `<b>${params[0]?.name || ''}</b> (Tổng: ${formatNumber(total)})<br/>`;
          params.forEach((p) => {
            const val = Number(p.value) || 0;
            if (val > 0) {
              html += `${p.marker} ${p.seriesName}: <b>${formatNumber(val)}</b><br/>`;
            }
          });
          return html;
        },
      },
      legend: {
        bottom: hasZoom ? 36 : 10,
        left: 'center',
        icon: 'rect',
        itemWidth: 16,
        itemHeight: 2,
        itemGap: 16,
        textStyle: {
          fontSize: 12,
          color: '#595959',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: hasZoom ? 75 : 45,
        top: '8%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: hasData ? categories : ['Không có dữ liệu'],
        axisLabel: {
          fontSize: 11,
          rotate: 0,
          formatter: (val: string) => {
            if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
              const [y, m, d] = val.split('-');
              return `${d}-${m}-${y}`;
            }
            return val;
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#e8e8e8',
          },
        },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: {
          formatter: (val: number) => formatNumber(val),
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#e8e8e8',
          },
        },
      },
      dataZoom: hasZoom
        ? [
            {
              type: 'slider',
              show: true,
              startValue: 0,
              endValue: 9,
              height: 18,
              bottom: 8,
              borderColor: 'transparent',
              backgroundColor: '#f5f5f5',
              fillerColor: 'rgba(24, 144, 255, 0.2)',
              handleStyle: {
                color: '#1890ff',
                borderColor: '#1890ff',
              },
            },
            {
              type: 'inside',
              startValue: 0,
              endValue: 9,
            },
          ]
        : [],
      series: trendChartData.series.map((s, idx) => ({
        name: s.name,
        type: 'line',
        smooth: false,
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: {
          color: FEATURE_COLORS[s.name] || COLOR_PALETTE[idx % COLOR_PALETTE.length],
        },
        data: s.data,
      })),
    };
  };

  return (
    <Card css={cardStyle}>
      <Spin spinning={loading}>
        <div css={cardHeaderStyle}>
          Lượt truy cập theo thời gian
        </div>
        <div style={{ padding: '16px 20px 12px' }}>
          <ReactECharts
            option={getLineOption()}
            style={{ height: '360px', width: '100%' }}
            notMerge={true}
          />
        </div>
      </Spin>
    </Card>
  );
};

const cardStyle = css`
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  box-shadow: none;
  height: 100%;
  .ant-card-body {
    padding: 0;
  }
`;

const cardHeaderStyle = css`
  text-align: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  font-size: 15px;
  color: #262626;
`;
